import React, { useMemo } from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { AlertCircle, ShieldCheck, Activity, GripVertical } from "lucide-react";
import { SortableEpicCard } from "../cards/SortableEpicCard";
import { Epic, Project, User, UserRole } from "../../types";
import { cn } from "../../utils/cn";

interface ListViewProps {
    epics: Epic[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    getPreviousScore: (id: string) => number | undefined | null;
    getProjectName?: (id?: string) => string;
    projects?: Project[];
    onUpdate?: (id: string, updates: Partial<Epic>) => void;
    users?: User[];
    userRole?: UserRole;
}

const PriorityBadge = ({ priority }: { priority?: string }) => {
    switch (priority?.toLowerCase()) {
        case 'critical': return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 uppercase tracking-wider flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Critical</span>;
        case 'high': return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 uppercase tracking-wider flex items-center gap-1"><Activity className="w-3 h-3" /> High</span>;
        case 'medium': return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 uppercase tracking-wider flex items-center gap-1"><Activity className="w-3 h-3" /> Medium</span>;
        case 'low': return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Low</span>;
        default: return null;
    }
};

interface ProjectLaneProps {
    lane: {
        id: string;
        name: string;
        priority?: string;
        owner?: string;
        epics: Epic[];
    };
    onSelect: (id: string) => void;
    selectedId: string | null;
    getPreviousScore: (id: string) => number | undefined | null;
    getProjectName?: (id?: string) => string;
    projects?: Project[];
    onUpdate?: (id: string, updates: Partial<Epic>) => void;
    users?: User[];
    userRole?: UserRole;
}

const ProjectLane = ({ lane, onSelect, selectedId, getPreviousScore, getProjectName, projects, onUpdate, users, userRole }: ProjectLaneProps) => {
    const { setNodeRef, isOver } = useDroppable({
        id: lane.id,
    });

    return (
        <div
            ref={setNodeRef}
            className={cn(
                "flex-shrink-0 w-full md:w-auto md:min-w-[350px] flex-1 flex flex-col h-full bg-slate-100/50 dark:bg-slate-900/40 rounded-2xl md:rounded-3xl border border-black/5 dark:border-white/5 backdrop-blur-3xl snap-center transition-colors",
                isOver && "bg-siemens-petrol/5 border-siemens-petrol/30"
            )}
        >
            {/* Lane Header */}
            <div className="p-4 border-b border-black/5 dark:border-white/5 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-black text-siemens-deep-blue dark:text-white leading-tight">
                        {lane.name}
                    </h3>
                    <PriorityBadge priority={lane.priority} />
                </div>
                <div className="flex items-center justify-between text-xs text-siemens-text-soft">
                    <span>{lane.owner || 'Unassigned'}</span>
                    <span className="font-bold bg-white dark:bg-slate-800 px-2 py-0.5 rounded-full">
                        {lane.epics.length}
                    </span>
                </div>
            </div>

            {/* Epics List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3 custom-scrollbar">
                <SortableContext
                    id={lane.id}
                    items={lane.epics.map(e => e.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {lane.epics.length === 0 ? (
                        <div className="h-32 flex items-center justify-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl opacity-50">
                            <span className="text-[10px] font-bold uppercase text-slate-400">Drop Epics Here</span>
                        </div>
                    ) : (
                        lane.epics.map((e) => (
                            <SortableEpicCard
                                key={e.id}
                                epic={e}
                                onSelect={onSelect}
                                selectedId={selectedId}
                                getPreviousScore={getPreviousScore}
                                getProjectName={getProjectName}
                                projects={projects}
                                onUpdate={onUpdate}
                                users={users}
                                userRole={userRole}
                            />
                        ))
                    )}
                </SortableContext>
            </div>
        </div>
    );
};

const priorityOrder: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };

export function ListView({
    epics,
    selectedId,
    onSelect,
    getPreviousScore,
    getProjectName,
    projects = [],
    onUpdate,
    users,
    userRole
}: ListViewProps) {
    const groupedEpics = useMemo(() => {
        // Helper to resolve PM user IDs to display names
        const getPmNames = (pmIds?: string[]): string => {
            if (!pmIds || pmIds.length === 0) return '';
            const names = pmIds
                .map(id => users?.find(u => u.id === id)?.displayName)
                .filter(Boolean);
            return names.length > 0 ? names.join(', ') : '';
        };

        // Group by project
        const groups: Record<string, Epic[]> = {};
        const unassigned: Epic[] = [];

        epics.forEach(e => {
            if (e.projectId && e.projectId !== 'project-legacy') {
                if (!groups[e.projectId]) groups[e.projectId] = [];
                groups[e.projectId].push(e);
            } else {
                unassigned.push(e);
            }
        });

        // Create project lanes
        const projectLanes = projects
            .filter(p => p.status === 'active' || groups[p.id]?.length > 0 || p.id === 'project-legacy')
            .map(p => {
                // If this is the legacy/unassigned bucket, merge unassigned epics
                let laneEpics = groups[p.id] || [];
                if (p.id === 'project-legacy') {
                    laneEpics = [...laneEpics, ...unassigned];
                }

                // If no epics and not active/legacy, we might filter it out (already handled by filter)
                // But we want to ensure project-legacy shows up if it has unassigned items
                if (p.id === 'project-legacy' && laneEpics.length === 0) return null;

                // If regular project has no epics and is not active, skip
                if (p.id !== 'project-legacy' && laneEpics.length === 0 && p.status !== 'active') return null;

                // Resolve PM names from user IDs
                const pmNames = getPmNames(p.pm);

                return {
                    id: p.id,
                    name: p.id === 'project-legacy' ? 'Legacy / Unassigned' : p.name,
                    priority: p.priority,
                    owner: pmNames || p.owner || '',
                    epics: laneEpics
                };
            })
            .filter((p): p is NonNullable<typeof p> => p !== null);

        // Check if we still have unassigned epics that weren't caught by a project-legacy (e.g. project-legacy deleted or missing)
        // If so, create a fallback lane
        const handledLegacy = projects.some(p => p.id === 'project-legacy');
        if (!handledLegacy && unassigned.length > 0) {
            projectLanes.push({
                id: 'legacy-unassigned-fallback',
                name: 'Legacy / Unassigned',
                priority: 'low',
                owner: 'Various',
                epics: unassigned
            });
        }

        // Sort projects by priority
        projectLanes.sort((a, b) => {
            const pA = priorityOrder[a.priority?.toLowerCase() || 'medium'] || 1;
            const pB = priorityOrder[b.priority?.toLowerCase() || 'medium'] || 1;
            if (pA !== pB) return pB - pA; // Descending priority
            return a.name.localeCompare(b.name);
        });

        return projectLanes;
    }, [epics, projects, users]);

    return (
        <div className="flex h-full gap-4 md:gap-8 p-4 md:p-10 overflow-x-auto pb-4 snap-x snap-mandatory md:snap-none">
            {groupedEpics.map(lane => (
                <ProjectLane
                    key={lane.id}
                    lane={lane}
                    onSelect={onSelect}
                    selectedId={selectedId}
                    getPreviousScore={getPreviousScore}
                    getProjectName={getProjectName}
                    projects={projects}
                    onUpdate={onUpdate}
                    users={users}
                    userRole={userRole}
                />
            ))}

            {groupedEpics.length === 0 && (
                <div className="flex items-center justify-center w-full h-full text-slate-400">
                    <div className="text-center">
                        <GripVertical className="w-12 h-12 mx-auto mb-2 opacity-20" />
                        <p className="font-bold uppercase tracking-widest text-xs">No Projects Found</p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ListView;
